/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import PDFViewer, { PDFViewerStyles } from "@eisinc/App/Components/PDFViewer/PDFViewer";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { AttributeGroup, DocumentGroup } from "@eisinc/App/Store/Reducers/ProductDetails/state";
import {
    getProductAttributeGroups,
    getProductDocumentGroups,
    getProductEmbedDocumentIds,
    getProductProperties
} from "@eisinc/App/Store/Selectors/ProductDetailsSelectors";
import TabGroup from "@eisinc/MobiusOverrides/TabGroup";
import { screen } from "@eisinc/XDL/Helpers";
import { printStyles } from "@eisinc/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { DocumentModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Tab, { TabProps } from "@insite/mobius/Tab";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({
    attributeGroups: getProductAttributeGroups(state, ownProps.product),
    documentGroups: getProductDocumentGroups(state, ownProps.product),
    properties: getProductProperties(state, ownProps.product),
    embedDocumentIds: getProductEmbedDocumentIds(state, ownProps.product),
});

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

type AssumedChildrenType = React.ReactElement<TabProps>[];

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsSpecificationsStyles {
    wrapper?: InjectableCss;
    attrGroupsWrapper?: InjectableCss;
    attrGroup?: InjectableCss;
    attrGroupTitle?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    printTitle?: TypographyPresentationProps;
    pfdStyles?: PDFViewerStyles;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    wrapper: {
        css: css`
            margin-top: 25px;

            ${screen("lte", "sm")`
                margin: 40px 0;
            `}
        `,
    },
    attrGroupsWrapper: {
        css: css`
            column-count: 2;
            column-gap: 100px;

            ${screen("lte", "lg")`
                column-gap: 40px;
            `}

            ${screen("lte", "sm")`
                column-count: 1;
                column-gap: 0;
            `}
        `,
    },
    attrGroup: {
        css: css`
            break-inside: avoid;
            page-break-inside: avoid;
            column-break-inside: avoid;
            margin-bottom: 25px;
        `,
    },
    attrGroupTitle: {
        variant: "h5",
        size: 18,
        lineHeight: "22px",
        css: css`
            text-align: center;
            margin-bottom: 10px;
            text-transform: capitalize;

            ${screen("lte", "sm")`
                font-size: 16px;
                line-height: 19px;
                text-align: left;
            `}
        `,
    },
    attributeTypeWrapper: {
        css: css`
            display: flex;
            border-bottom: 3px solid ${getColor("common.ui3")};
            min-height: 40px;
            padding-top: 10px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
        size: 14,
        lineHeight: "20px",
        css: css`
            flex: 0 0 50%;
            padding: 0 25px;
            text-align: right;

            ${screen("lte", "sm")`
                padding: 0;
                flex: 0 0 40%;
                font-size: 12px;
                text-align: left;
            `}
        `,
    },
    attributeTypeValuesText: {
        size: 14,
        lineHeight: "20px",
        css: css`
            flex: 0 0 50%;
            padding: 0 25px;

            ${screen("lte", "sm")`
                flex: 0 0 60%;
                font-size: 12px;
            `}
        `,
    },
    printTitle: {
        variant: "h3",
        size: 40,
        css: css`
            ${printStyles.showOnPrint}
        `,
    },
    pfdStyles: {
        documentLink: {
            css: css`
                width: 100%;
                justify-content: center;

                @media (max-width: 768px) {
                    justify-content: flex-start;
                }
            `,
        },
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    attributeGroups,
    documentGroups,
    product: { specifications },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
    properties,
    embedDocumentIds,
}) => {
    const renderingDocuments = showDocuments && documentGroups?.length;
    const renderingAttributes = showAttributes && attributeGroups?.length;
    const renderingSpecifications = specifications && specifications.length;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";
    const parsedEmbedDocumentIds = embedDocumentIds || [];

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }

    const tabsAttributes: AssumedChildrenType = [];
    const tableSpecs: AssumedChildrenType = [];
    const tabsDocuments: AssumedChildrenType = [];

    if (renderingAttributes) {
        tabsAttributes.push(
            <Tab
                headline={translate("Attributes")}
                data-test-selector="productDetails_specifications_attributes"
                key="attributes"
                tabKey="attributes"
            >
                <Typography {...styles.printTitle}>{translate("Attributes")}</Typography>
                <StyledWrapper {...styles.attrGroupsWrapper}>
                    {attributeGroups?.map((group: AttributeGroup) => (
                        <StyledWrapper {...styles.attrGroup} key={group.GroupName.toString()}>
                            <Typography {...styles.attrGroupTitle}>{group.GroupName}</Typography>
                            {/* TODO: Check with backend to fix camelCase issue. */}
                            {group.GroupedAttributeTypes?.map((attributeType: any) => (
                                <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.Id.toString()}>
                                    <Typography {...styles.attributeTypeLabelText}>
                                        {attributeType.Label || attributeType.name}
                                    </Typography>
                                    <Typography {...styles.attributeTypeValuesText}>
                                        {attributeType
                                            .AttributeValues!.map((attributeValue: any) => attributeValue.ValueDisplay)
                                            .join(", ")}
                                    </Typography>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </Tab>,
        );
    }

    if (specifications) {
        specifications.every(specification => {
            tableSpecs.push(
                <Tab
                    headline={specification.nameDisplay}
                    data-test-selector="productDetails_specifications_specification"
                    key={specification.id.toString()}
                    tabKey={specification.id.toString()}
                >
                    <Typography {...styles.printTitle}>{specification.nameDisplay}</Typography>
                    {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                </Tab>,
            );
            return true;
        });
    }

    if (renderingDocuments) {
        tabsDocuments.push(
            <Tab
                headline={translate("Documents")}
                data-test-selector="productDetails_specifications_documents"
                tabKey="documents"
                key="documents"
            >
                <StyledWrapper {...styles.attrGroupsWrapper}>
                    <Typography {...styles.printTitle}>{translate("Documents")}</Typography>
                    {documentGroups?.map((group: DocumentGroup) => (
                        <StyledWrapper
                            {...styles.attrGroup}
                            key={`${group?.documents[0]?.id}_${group.groupName.toString()}`}
                        >
                            <Typography {...styles.attrGroupTitle}>{group.groupName}</Typography>
                            {group.documents?.map((document: DocumentModel) => (
                                <PDFViewer
                                    key={document.id.toString()}
                                    filePath={document.filePath}
                                    fileName={document.name}
                                    dataTestSelector={`productDetails_${document.id}`}
                                    asLink={true}
                                    newTab={false}
                                    // https://xngage.atlassian.net/browse/EIS-3913 - foce all attachments to be embedded
                                    extraQueryStringParams={{
                                        doNotEmbed: false,
                                    }}
                                    extendedStyles={styles.pfdStyles}
                                />
                            ))}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </Tab>,
        );
    }

    let tabs = tableSpecs;
    if (documentsFirst) {
        tabs = [...tabsDocuments, ...tabs];
    } else {
        tabs = [...tabs, ...tabsDocuments];
    }

    if (attributesFirst) {
        tabs = [...tabsAttributes, ...tabs];
    } else {
        tabs = [...tabs, ...tabsAttributes];
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <TabGroup data-test-selector="productDetails_specifications">{tabs}</TabGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsSpecifications)),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
